import { MapObject } from "@iventis/domain-model/model/mapObject";
import { Node } from "@iventis/domain-model/model/node";
import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ObjectDetailsComponent } from "@iventis/object-details";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { localGeoJSONObjectToMapObject } from "@iventis/map-engine/src/utilities/converters";
import { MapLayer } from "@iventis/domain-model/model/mapLayer";
import { canEditAll } from "@iventis/utilities";
import { DataField } from "@iventis/domain-model/model/dataField";
import { IAssetService } from "@iventis/components";
import { Authorisation } from "@iventis/domain-model/model/authorisation";
import { updateMapObjectsQuantityValue } from "@iventis/datafields";
import { IventisErrorBoundary } from "@iventis/error-boundaries";
import { ErrorBoundaryObjectDetails } from "@iventis/error-boundaries/src/error-boundary-object-details";
import { PopoverContainer } from "@iventis/components/src/popover-container";
import { RootDispatch, RootState } from "./state/root.store";
import { patchMapObjectsDataFieldValues } from "./state/map.slice.thunks";
import { sidebar } from "./state/sidebar.slice";
import { map } from "./state/map.slice";

export const ObjectDetailsContainer: React.FC<{
    mapObjectIdsSelected: string[];
    mapLayersSelected: MapLayer[];
    updateMapObjectNodeNames: (mapObjects: MapObject[]) => void;
    updateGeom: (mapObject: MapObject) => void;
    isObjectDetailsMinimised: boolean;
    setObjectDetailsMinimised: (isMinimised: boolean) => void;
    setTransitionCompleted: (transitionCompleted: boolean) => void;
    transitionCompleted: boolean;
    selectedLayerNodes: Node[];
    getAsset: IAssetService["getAsset"];
    userId?: string;
    layerPermissions: { [layerId: string]: Authorisation };
}> = ({
    mapObjectIdsSelected,
    mapLayersSelected,
    updateMapObjectNodeNames,
    updateGeom,
    isObjectDetailsMinimised,
    setObjectDetailsMinimised,
    transitionCompleted,
    setTransitionCompleted,
    userId,
    selectedLayerNodes,
    getAsset,
    layerPermissions,
}) => {
    const dispatch: RootDispatch = useDispatch();
    const translate = useIventisTranslate();

    const { objectsByLayer, objectsByLayerStamp, unitOfMeasurementValue, projectDataFields } = useSelector(
        (s: RootState) => ({
            objectsByLayer: s.mapReducer.mapModule.geoJSON.value,
            objectsByLayerStamp: s.mapReducer.mapModule.geoJSON.stamp,
            unitOfMeasurementValue: s.mapReducer.mapModule.unitOfMeasurement.value,
            unitOfMeasurementStamp: s.mapReducer.mapModule.unitOfMeasurement.stamp,
            projectDataFields: s.mapReducer.mapModule.projectDataFields,
        }),
        (previousState, currentState) =>
            previousState.objectsByLayerStamp === currentState.objectsByLayerStamp &&
            previousState.unitOfMeasurementStamp === currentState.unitOfMeasurementStamp &&
            previousState.projectDataFields?.length === currentState.projectDataFields?.length
    );

    const objects = useMemo(
        () =>
            Object.values(objectsByLayer)
                .flat()
                .filter((object) => mapObjectIdsSelected.includes(object.objectId))
                .map((object) => localGeoJSONObjectToMapObject(object)),
        [objectsByLayerStamp, mapObjectIdsSelected]
    );
    const loading = objects.length !== mapObjectIdsSelected.length;

    const saveMapObjectChanges = async (mapObjects: MapObject[]) => {
        updateMapObjectNodeNames(mapObjects);
        if (mapObjects.length === 0) {
            throw new Error("No map object found when attempting to save changes");
        } else {
            await dispatch(
                patchMapObjectsDataFieldValues({
                    mapObjects: updateMapObjectsQuantityValue(mapLayersSelected, mapObjects, projectDataFields),
                    fulfilledNotification: true,
                })
            );
        }
    };

    const handleManageAttributesClicked = () => {
        if (mapLayersSelected.length !== 1) {
            throw new Error("Can't edit a layer when more than one layer is selected");
        }
        dispatch(sidebar.actions.setModalLayer(mapLayersSelected[0].id));
    };

    const handleEditAttributesClicked = (_: React.MouseEvent, dataField: DataField) => {
        if (dataField == null || mapLayersSelected.length !== 1) {
            throw new Error("No attribute available to edit");
        }
        dispatch(sidebar.actions.setAttributeEditingId({ attributeId: dataField.id, attributeLayerId: mapLayersSelected[0].id }));
    };

    const canUserEdit = useMemo(() => canEditAll(selectedLayerNodes), [selectedLayerNodes]);

    return (
        <PopoverContainer className="popup" testId="object-details-panel">
            <IventisErrorBoundary
                size="custom"
                customComponent={<ErrorBoundaryObjectDetails />}
                action={{
                    actionText: "Close",
                    action: () => {
                        dispatch(map.actions.setMapObjectsSelected({ newSelection: [], addToUndoStack: false }));
                    },
                }}
            >
                <ObjectDetailsComponent
                    mapObjectsSelected={objects}
                    mapLayersSelected={mapLayersSelected}
                    loading={loading}
                    // eslint-disable-next-line no-return-await
                    saveChanges={async (mapObjects: MapObject[]) => await saveMapObjectChanges(mapObjects)}
                    updateGeom={(mapObject) => updateGeom(mapObject)}
                    translate={translate}
                    onManageAttributesClicked={handleManageAttributesClicked}
                    userId={userId}
                    isObjectDetailsMinimised={isObjectDetailsMinimised}
                    setObjectDetailsMinimised={setObjectDetailsMinimised}
                    setTransitionCompleted={setTransitionCompleted}
                    transitionCompleted={transitionCompleted}
                    canUserEdit={canUserEdit}
                    unitOfMeasurement={unitOfMeasurementValue}
                    onEditAttributeClicked={handleEditAttributesClicked}
                    getAsset={getAsset}
                    projectDataFields={projectDataFields}
                    layerPermissions={layerPermissions}
                />
            </IventisErrorBoundary>
        </PopoverContainer>
    );
};
